import { KeycloakService } from "keycloak-angular";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";

import { Role } from "../../shared/utils/role.model";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly translateService: TranslateService,
  ) { }

  getUsername() {
    return this.keycloakService.getUsername();
  }

  /**
   * The Keycloak account endpoint is a separate origin, so this call fails whenever the client's
   * Web Origins do not cover the host the Backoffice is served from. The profile is display-only
   * data — name, phone — while the roles and groups the UI gates on come from the token, so a
   * failure here resolves to null rather than rejecting. Throwing used to abort whatever the
   * caller was doing next, which in the sidebar meant the whole navigation tree was never built.
   */
  async getProfile(): Promise<any> {
    try {
      if (this.isLoggedIn()) {
        const profile = await this.keycloakService.loadUserProfile();
        return profile;
      }
      return null;
    } catch (error) {
      console.warn("Unable to load the Keycloak user profile:", error);
      return null;
    }
  }

  logout() {
    // Read the exact translations object for the user's selected language directly,
    // instead of relying on TranslateService.currentLang/instant(), which can lag
    // behind sessionStorage when this runs before the service has fully caught up.
    const currentLang = sessionStorage.getItem("lang") || this.translateService.currentLang || "fr";
    this.translateService.getTranslation(currentLang).subscribe((translations) => {
      const t = (key: string): string =>
        key.split(".").reduce((obj: any, part: string) => obj?.[part], translations) ?? key;

      Swal.fire({
        showCloseButton: true,
        title: t("APP_USER.LOGOUT_CONFIRM_TITLE"),
        text: t("APP_USER.LOGOUT_CONFIRM_TEXT"),
        icon: "warning",
        showCancelButton: true,
        iconColor: "#dc2626",
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#094259",
        confirmButtonText: t("APP_USER.LOGOUT"),
        cancelButtonText: t("APP_SWAL.NO"),
        customClass: {
          popup: "logout-swal",
          confirmButton: "btn-confirm-large logout-swal-confirm",
          cancelButton: "btn-confirm-large",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.keycloakService.logout();
        }
      });
    });
  }

  isLoggedIn(): boolean {
    return this.keycloakService.isLoggedIn();
  }

  accountManagement(): Promise<void> {
    return this.keycloakService.getKeycloakInstance().accountManagement();
  }

  getUserRoles(): string[] {
    return this.keycloakService.getUserRoles();
  }

  getUserGroups(): string[] {
    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
    if (tokenParsed) {
      const groups = tokenParsed["groups"];
      return Array.isArray(groups)
        ? groups.filter((group): group is string => typeof group === "string")
        : [];
    }
    return [];
  }

  hasRole(role: string): boolean {
    return this.getUserRoles().indexOf(role) > -1;
  }

  /** Global chatbots belong to no company: only the SOLUTION team creates and changes them. */
  canManageGlobalBots(): boolean {
    return this.isUserAuthorizedByGroup("/SOLUTION") && this.hasRole(Role.ADMIN_SOLUTION_UI);
  }

  /** Company admins can aggregate data across all WhatsApp numbers. */
  canViewAllCompanyPhones(): boolean {
    return this.hasRole(Role.COMPANY_MANAGE);
  }

  getCompanyId(): string | undefined {
    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
    return tokenParsed?.["companyId"];
  }

  isUserAuthorizedByGroup(groupName: any): boolean {
    return typeof groupName === "string" && this.hasAnyGroup([groupName]);
  }

  hasAnyGroup(groupNames: readonly string[]): boolean {
    const requiredGroups = groupNames
      .map((group) => this.normalizeGroup(group))
      .filter(Boolean);
    if (requiredGroups.length === 0) {
      return true;
    }

    const userGroups = this.getUserGroups().map((group) =>
      this.normalizeGroup(group),
    );
    return requiredGroups.some((required) =>
      userGroups.some(
        (userGroup) => userGroup === required || userGroup.endsWith(required),
      ),
    );
  }

  private normalizeGroup(group: string): string {
    const normalized = group.trim().replace(/\/+$/, "");
    if (!normalized) {
      return "";
    }
    return normalized.startsWith("/") ? normalized : `/${normalized}`;
  }
}
